import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Form,
  TextField,
  Select,
  Button,
  Toast,
  ChoiceList,
  Checkbox,
  Banner,
  List,
  TextContainer,
  FormLayout,
  BlockStack,
  InlineStack,
  Box,
  ButtonGroup,
  Frame,
  Text,
  Tag
} from '@shopify/polaris';
import { PlusIcon } from '@shopify/polaris-icons';
import { SaveBar, useAppBridge } from '@shopify/app-bridge-react';
import useAxiosClient from '../axiosClient';
import { useNavigate } from 'react-router-dom';
import { SortableList } from "../SortableList";
import { useTranslation } from 'react-i18next';
import '../../i18n';

export default function AddTasklist() {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const appBridge = useAppBridge();
  const { t } = useTranslation();
  
  // Modified state for task list

   const [taskListInfo, setTaskListInfo] = useState({
      name: '',
      description: '',
      assignedTo: [], // Changed to array for multi-select
      selectedLocationType: 'All',
      selectedLocations: [],
      frequency: {
        daily: false,
        weekly: false,
        monthly: false
      },
    });
  const assignToOptions = [
    { label: t('staff'), value: 'Staff' },
    { label: t('manager'), value: 'Manager' },
  ];
  // State for tasks
  const [tasks, setTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [taskInputError, setTaskInputError] = useState('');

  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [savebar, setSaveBar] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [errors, setErrors] = useState({});
  const [showErrorBanner, setShowErrorBanner] = useState(false); // New state for error banner visibility
  const originalData = useRef(taskListInfo);
  
  // Update showErrorBanner whenever errors change
  useEffect(() => {
    setShowErrorBanner(Object.keys(errors).length > 0);
  }, [errors]);
  
  // Handle adding a new task
  const handleAddTask = () => {
    if (!newTaskTitle.trim()) {
      setTaskInputError(t('task_title_cannot_be_empty'));
      return;
    }
    
    // Create new task with a unique ID
    const newTask = {
      id: `task-${Date.now()}`,
      title: newTaskTitle.trim(),
      status: 'active',
    };
    
    // Add to task list
    setTasks([...tasks, newTask]);
    setNewTaskTitle('');
    setShowTaskInput(false);
    setTaskInputError('');
    setIsDirty(true);
    
    // Clear the tasks error if it exists
    if (errors.tasks) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.tasks;
        return newErrors;
      });
    }
  };
  

  // Track input changes and mark form as dirty
  const handleChange = (field) => (value) => {
    setTaskListInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
    
    // Clear the error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle frequency checkboxes - modified to allow only one selection at a time
  const handleFrequencyChange = (key) => (checked) => {
    let newFrequency;
    
    if (checked) {
      // If this checkbox is being checked, uncheck all others
      newFrequency = {
        daily: key === 'daily',
        weekly: key === 'weekly',
        monthly: key === 'monthly'
      };
      setTaskListInfo((prev) => ({
        ...prev,
        frequency: newFrequency
      }));
    } else {
      // If this checkbox is being unchecked, just uncheck it
      newFrequency = {
        daily: key === 'daily' ? false : taskListInfo.frequency.daily,
        weekly: key === 'weekly' ? false : taskListInfo.frequency.weekly,
        monthly: key === 'monthly' ? false : taskListInfo.frequency.monthly
      };
      setTaskListInfo((prev) => ({
        ...prev,
        frequency: newFrequency
      }));
    }
    
    setIsDirty(true);
    
    // Clear frequency error if any frequency is selected
    if ((newFrequency.daily || newFrequency.weekly || newFrequency.monthly) && errors.frequency) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.frequency;
        return newErrors;
      });
    }
    
    // Add frequency error back if no frequency is selected
    if (!newFrequency.daily && !newFrequency.weekly && !newFrequency.monthly) {
      setErrors((prev) => ({
        ...prev,
        frequency: t('at_least_one_frequency_must_be_selected')
      }));
    }
  };

  const handleAssignToChange = (selectedValues) => {
    setTaskListInfo(prev => ({
      ...prev,
      assignedTo: selectedValues
    }));
    setIsDirty(true);
    
    // Clear error for assignedTo if it exists
    if (errors.assignedTo) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.assignedTo;
        return newErrors;
      });
    }
  };

  const removeAssignee = (assigneeToRemove) => {
    setTaskListInfo(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.filter(assignee => assignee !== assigneeToRemove)
    }));
    setIsDirty(true);
  };
  
  // Handle task deletion
  const handleDeleteTask = (taskId) => {
    const updatedTasks = tasks.filter(task => task.id !== taskId);
    setTasks(updatedTasks);
    setIsDirty(true);
    
    // If all tasks are deleted, add the tasks error back
    if (updatedTasks.length === 0) {
      setErrors((prev) => ({
        ...prev,
        tasks: t('please_add_at_least_one_task_to_the_task_list')
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Task List Validation
    if (!taskListInfo.name.trim()) {
      newErrors.name = t('task_list_name_is_required');
    } else if (taskListInfo.name.length < 2) {
      newErrors.name = t('task_list_name_must_be_at_least_2_characters');
    }
    
    // Assign To Validation
    if (!taskListInfo.assignedTo || taskListInfo.assignedTo.length === 0) {
      newErrors.assignedTo = t('please_select_at_least_one_assignee');
    }
    
    // Location Validation
    if (taskListInfo.selectedLocationType === 'Specific' && 
        (!taskListInfo.selectedLocations || taskListInfo.selectedLocations.length === 0)) {
      newErrors.locations = t('please_select_at_least_one_location');
    }
    
    // Frequency Validation
    if (!taskListInfo.frequency.daily && !taskListInfo.frequency.weekly && !taskListInfo.frequency.monthly) {
      newErrors.frequency = t('at_least_one_frequency_must_be_selected');
    }
    
    // Tasks Validation
    if (tasks.length === 0) {
      newErrors.tasks = t('please_add_at_least_one_task_to_the_task_list');
    }
    
    // Set the errors and return validation result
    setErrors(newErrors);
    
    // If there are any errors, show a toast message
    // if (Object.keys(newErrors).length > 0) {
    //   setShowToast(true);
    //   setToastContent('Please fill in all required fields correctly');
    // }
    
    return Object.keys(newErrors).length === 0;
  };

  const valueRef = useRef({
    taskListInfo,
    tasks
  });
  
  useEffect(() => {
    valueRef.current = {
      taskListInfo,
      tasks
    };
  }, [taskListInfo, tasks]);

  const handleSave = async () => {
    if (!validateForm()) {
      // setShowToast(true);
      // setToastContent('Please fix the validation errors before saving');
      setShowErrorBanner(true); // Ensure error banner is visible
      appBridge.saveBar.hide('create-tasklist-save-bar');
      return;
    }
    
    try {
      setIsSaving(true);
      const allLocationNames = locations.map(location => location.name).join(', ');
      
      // Convert assignedTo array to string for API
      const assignedToString = valueRef.current.taskListInfo.assignedTo.join(', ');
      const dataToSend = {
        ...valueRef.current.taskListInfo,
        locations: valueRef.current.taskListInfo.selectedLocationType === 'All' 
          ? allLocationNames
          : valueRef.current.taskListInfo.selectedLocations.join(', '),
          assignedTo: assignedToString,
        tasks: valueRef.current.tasks
      };
  
      const response = await axiosClient.post('/create_tasklists', dataToSend);
      if (response.data.success) {
        originalData.current = taskListInfo;
        setIsDirty(false);
        shopify.toast.show(t('task_list_created'));
        appBridge.saveBar.hide('create-tasklist-save-bar');
        navigate('/Tasklist');
      } else {
        // Handle server validation errors
        if (response.data.errors) {
          setErrors(response.data.errors);
          setShowErrorBanner(true);
          // Show first error message in toast
          const firstError = Object.values(response.data.errors)[0];
          // shopify.toast.show(firstError[0] || 'Validation failed', { error: true });
        } else {
          // shopify.toast.show(response.data.message || 'Failed to create task list', { error: true });
        }
        appBridge.saveBar.hide('create-tasklist-save-bar');
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        // Handle validation errors from server
        setErrors(error.response.data.errors);
        setShowErrorBanner(true);
        const firstError = Object.values(error.response.data.errors)[0];
        // shopify.toast.show(firstError[0] || 'Validation failed', { error: true });
      } else {
        setToastContent(t('error_creating_task_list'));
        // shopify.toast.show('Error creating task list', { error: true });
      }
      appBridge.saveBar.hide('create-tasklist-save-bar');
    } finally {
      setIsSaving(false);
    }
  };

  // Discard changes and reset form
  const handleDiscard = () => {
    setTaskListInfo(originalData.current);
    setTasks([]);
    setIsDirty(false);
    setErrors({});
    setShowErrorBanner(false);
    appBridge.saveBar.hide('create-tasklist-save-bar');
  };

  // Fetch locations for the form
  const fetchLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await axiosClient.get('/locations');
      if (response.data.success) {
        setLocations(response.data.locations);
      } else {
        setToastContent(t('failed_to_load_locations'));
        setShowToast(true);
      }
    } catch (error) {
      setToastContent(t('error_loading_locations'));
      setShowToast(true);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Fetch locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);


  
  // Function to dismiss the error banner
  const dismissErrorBanner = () => {
    setShowErrorBanner(false);
  };

  const handleBackClick = useCallback(() => {
    if (isDirty) {
      // If there are unsaved changes, show confirmation dialog
      appBridge.saveBar.leaveConfirmation()
        .then(() => {
          // User confirmed leaving or no changes to save
          navigate("/Tasklist");
        })
        .catch(() => {
          // User cancelled leaving
        });
    } else {
      // No unsaved changes, navigate directly
      navigate("/Tasklist");
    }
  }, [isDirty, appBridge, navigate]);

  return (
    <Page
      title={t('create_task_list')}
      fullWidth
      backAction={{
        content: t('task_lists'),
        onAction: handleBackClick
      }}
    >
      {showErrorBanner && Object.keys(errors).length > 0 && (
        <Box paddingBlockEnd="400">
          <Banner
            title={t('please_correct_the_following_errors')}
            tone="critical"
            onDismiss={dismissErrorBanner}
          >
            <List type="bullet">
              {Object.entries(errors).map(([field, messages]) => (
                <List.Item key={field}>
                  {`${field.charAt(0).toUpperCase() + field.slice(1)}: ${Array.isArray(messages) ? messages[0] : messages}`}
                </List.Item>
              ))}
            </List>
          </Banner>
        </Box>
      )}
      <div style={{marginTop:'10px'}}></div>

      <Form onSubmit={handleSave}>
        <Layout>
          {/* Task List Information */}
          <Layout.AnnotatedSection
            title={t('task_list_details')}
            description={t('enter_your_task_list_name_and_description')}
          >
            <Card sectioned>
              <FormLayout>
                <TextField
                  label={t('task_list_name')}
                  value={taskListInfo.name}
                  onChange={handleChange('name')}
                  placeholder={t('enter_task_list_name')}
                  error={errors.name && Array.isArray(errors.name) ? errors.name[0] : errors.name}
                  autoComplete="off"
                  required
                />
                <TextField
                  label={t('description')}
                  value={taskListInfo.description}
                  onChange={handleChange('description')}
                  placeholder={t('describe_the_purpose_of_this_task_list')}
                  multiline={4}
                  error={errors.description}
                  autoComplete="off"
                />
              </FormLayout>
            </Card>
          </Layout.AnnotatedSection>

          {/* Assign To Section */}
<Layout.AnnotatedSection
            title={t('assigned_to')}
            description={t('choose_who_should_be_responsible_for_completing_this_task_list')}
          >
            <Card sectioned>
              <BlockStack gap="400">
                {errors.assignedTo && (
                  <Banner tone="critical">{errors.assignedTo}</Banner>
                )}

                <ChoiceList
                  title={t('assign_to')}
                  allowMultiple
                  choices={assignToOptions}
                  selected={taskListInfo.assignedTo}
                  onChange={handleAssignToChange}
                />
                
                {/* Show selected assignees as tags */}
                {taskListInfo.assignedTo.length > 0 && (
                  <Box paddingBlockStart="300">
                    <TextContainer>
                      <Text variant="headingXs">{t('selected_assignees')}:</Text>
                      <InlineStack gap="200" wrap>
                        {taskListInfo.assignedTo.map(assignee => (
                          <Tag 
                            key={assignee}
                            onRemove={() => removeAssignee(assignee)}
                          >
                            {assignee}
                          </Tag>
                        ))}
                      </InlineStack>
                    </TextContainer>
                  </Box>
                )}
              </BlockStack>
            </Card>
          </Layout.AnnotatedSection>

          {/* Locations */}
          <Layout.AnnotatedSection
            title={t('locations')}
            description={t('select_the_locations_where_this_task_list_applies')}
          >
            <Card sectioned>
              <InlineStack align="start" gap="400">
                <ChoiceList
                  title=""
                  choices={[
                    { label: t('all_locations'), value: 'All' },
                    { label: t('specific_locations'), value: 'Specific' },
                  ]}
                  selected={[taskListInfo.selectedLocationType]}
                  onChange={(value) =>
                    setTaskListInfo((prev) => ({ 
                      ...prev, 
                      selectedLocationType: value[0],
                      selectedLocations: value[0] === 'All' ? [] : prev.selectedLocations 
                    }))
                  }
                />
              </InlineStack>
              
              {taskListInfo.selectedLocationType === 'Specific' && (
                <Box paddingBlockStart="400" paddingInline="400">
                  <TextContainer>
                    <p className="font-semibold">{t('available_locations')}</p>
                    {errors.locations && (
                      <Banner tone="critical">{errors.locations}</Banner>
                    )}
                  </TextContainer>
                  
                  {isLoadingLocations ? (
                    <Box paddingBlockStart="400">{t('loading_locations')}</Box>
                  ) : locations.length === 0 ? (
                    <Box paddingBlockStart="400">{t('no_locations_available')}</Box>
                  ) : (
                    <BlockStack gap="200">
                      {locations.map((location) => (
                        <Checkbox
                          key={location.id}
                          label={
                            <>
                              <strong>{location.name}</strong>
                              <div style={{ fontSize: '0.85rem', color: 'var(--p-text-subdued)' }}>
                                {location.address}
                              </div>
                            </>
                          }
                          checked={taskListInfo.selectedLocations?.includes(location.name)}
                          onChange={(checked) => {
                            setTaskListInfo((prev) => ({
                              ...prev,
                              selectedLocations: checked
                                ? [...(prev.selectedLocations || []), location.name]
                                : (prev.selectedLocations || []).filter(name => name !== location.name)
                            }));
                            setIsDirty(true);
                          }}
                        />
                      ))}
                    </BlockStack>
                  )}
                </Box>
              )}
            </Card>
          </Layout.AnnotatedSection>

          {/* Frequency */}
          <Layout.AnnotatedSection
            title={t('timeline')}
            description={t('choose_how_frequently_this_task_list_should_be_completed')}
          >
            <Card sectioned>
              <TextContainer>
                {errors.frequency && (
                  <Box paddingBlockStart="300">
                    <Banner tone="critical">{errors.frequency}</Banner>
                  </Box>
                )}
              </TextContainer>
              
              <BlockStack gap="300" padding="400">
                <Checkbox
                  label={t('daily')}
                  checked={taskListInfo.frequency.daily}
                  onChange={handleFrequencyChange('daily')}
                  helpText={t('this_task_list_needs_to_be_completed_every_day')}
                />
                <Checkbox
                  label={t('weekly')}
                  checked={taskListInfo.frequency.weekly}
                  onChange={handleFrequencyChange('weekly')}
                  helpText={t('this_task_list_needs_to_be_completed_every_week')}
                />
                <Checkbox
                  label={t('monthly')}
                  checked={taskListInfo.frequency.monthly}
                  onChange={handleFrequencyChange('monthly')}
                  helpText={t('this_task_list_needs_to_be_completed_every_month')}
                />
              </BlockStack>
            </Card>
          </Layout.AnnotatedSection>

          {/* Task Management Section */}
          <Layout.AnnotatedSection
            title={t('tasks')}
            description={t('add_and_arrange_the_tasks_that_need_to_be_completed')}
          >
            <Card sectioned>
              <BlockStack gap="400">
                <TextContainer>
                  <Text variant="headingSm">{t('task_items')}</Text>
                  <Text as="p" color="subdued">
                    {t('add_tasks_to_your_list_drag_to_reorder_them_as_needed')}
                  </Text>
                  {errors.tasks && (
                    <Banner tone="critical">{errors.tasks}</Banner>
                  )}
                </TextContainer>

                {/* Show existing tasks using SortableList if any exist */}
                {tasks.length > 0 && (
                  <Box paddingBlockStart="300">
                    <SortableList 
                      items={tasks} 
                      setItems={setTasks} 
                      onDelete={handleDeleteTask}
                      setIsDirty={setIsDirty} 
                    />
                  </Box>
                )}

                {/* Task input area - shown when button is clicked */}
                {showTaskInput ? (
                  <BlockStack gap="300">
                    <TextField
                      label={t('task_title')}
                      value={newTaskTitle}
                      onChange={setNewTaskTitle}
                      autoComplete="off"
                      placeholder={t('enter_task_title')}
                      error={taskInputError}
                      autoFocus
                    />
                    <InlineStack gap="200">
                      <Button onClick={handleAddTask} variant="primary">{t('add_task')}</Button>
                      <Button onClick={() => {
                        setShowTaskInput(false);
                        setTaskInputError('');
                        setNewTaskTitle('');
                      }}>{t('cancel')}</Button>
                    </InlineStack>
                  </BlockStack>
                ) : (
                  <Box paddingBlockStart="300">
                    <Button 
                      onClick={() => setShowTaskInput(true)} 
                      icon={PlusIcon}
                    >
                      {t('add_task')}
                    </Button>
                  </Box>
                )}
                
                {/* Empty state if no tasks */}
                {!showTaskInput && tasks.length === 0 && (
                  <Box
                    paddingBlock="400"
                    background="bg-surface-secondary"
                    borderRadius="200"
                    padding="400"
                  >
                    <Text as="p" color="subdued">
                      {t('no_tasks_added_yet_click_add_task_to_create_your_first_task')}
                    </Text>
                  </Box>
                )}
              </BlockStack>
            </Card>
          </Layout.AnnotatedSection>
        </Layout>
      </Form>
      <div style={{marginTop:"20px"}}></div>

      {isDirty && (
        <SaveBar
          id="create-tasklist-save-bar"
          open={isDirty}
        >
          <button
            type="button"
            variant="primary"
            onClick={async () => {
              try {
                await handleSave();
                setIsDirty(false);
              } catch (err) {
                // Error handled silently
              }
            }}
          >
            {t('save')}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
          >
            {t('discard')}
          </button>
        </SaveBar>
      )}

      {showToast && (
        <Frame>
          <Toast
            content={toastContent}
            error={Object.keys(errors).length > 0}
            onDismiss={() => setShowToast(false)}
            duration={5000}
          />
        </Frame>
      )}
    </Page>
  );
}